import {
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
  TextCopier,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import Autocomplete from '@mui/material/Autocomplete';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { PartnershipType } from '../../../../../types';
import { ClientType } from '../../../types';
import {
  capitalizeFirstLetterAndSpaceSnakeString,
  selectSorterFunction,
} from '../../../utils/bills-utils';
import { ClientFilter, InsurerFilter, PartnershipFilter } from '../../form';
import {
  allHoldTypeVals,
  InsurersType,
} from '../bill-grid-components/bill-management-types';
import {
  AggregateHoldsFiltersProps,
  AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS,
  AGGREGATE_HOLDS_DEFAULT_PAGE_SIZE,
  DEFAULT_HOLD_TYPES,
  HoldTypeOption,
} from './aggregate-holds-types';

// Common flex styling applied to hold reasons, clients, insurers, and partnerships
const FLEX_FILTER_CONTAINER_SX = {
  flex: '1 1 200px',
  minWidth: 160,
  maxWidth: 450,
} as const;

const filterValidationSchema = yup.object({
  holdTypes: yup
    .array()
    .of(
      yup.object({
        value: yup.string().defined(),
        label: yup.string().defined(),
      }),
    )
    .min(1, 'Hold reasons field must have at least 1 item')
    .defined(),
  dateRange: yup
    .array()
    .of(yup.date().nullable().defined())
    .min(2)
    .max(2)
    .defined(),
  minTotalImpact: yup.number().nullable().defined(),
  clients: yup
    .array()
    .of(
      yup.object({
        id: yup.number().defined(),
        identifier: yup.string().defined(),
        name: yup.string().defined(),
      }),
    )
    .defined(),
  insurers: yup
    .array()
    .of(
      yup.object({
        id: yup.number().defined(),
        identifier: yup.string().defined(),
        name: yup.string().defined(),
      }),
    )
    .defined(),
  partnerships: yup
    .array()
    .of(
      yup.object({
        id: yup.number().defined(),
        name: yup.string().defined(),
        allowCohortExpansion: yup.boolean().defined(),
        partnershipType: yup.string().defined(),
        requiresStateCredentials: yup.boolean().defined(),
      }),
    )
    .defined(),
});

interface FilterFormValues {
  holdTypes: HoldTypeOption[];
  dateRange: (Date | null)[];
  minTotalImpact: number | null;
  clients: ClientType[];
  insurers: InsurersType[];
  partnerships: PartnershipType[];
}

const AggregateHoldsFilters = ({
  filterParams,
  setFilterParams,
  setPaginationModel,
  clients,
  clientsError,
  clientsLoading,
  insurers,
  insurersError,
  insurersLoading,
  partnerships,
  partnershipsError,
  partnershipsLoading,
}: AggregateHoldsFiltersProps): JSX.Element => {
  const getCurrentUrl = (): string => window.location.href;
  const holdTypesSelectOptions: HoldTypeOption[] = allHoldTypeVals()
    .map(val => ({
      value: val,
      label: capitalizeFirstLetterAndSpaceSnakeString(val),
    }))
    .sort((a, b) => selectSorterFunction(a.label, b.label));

  const formValues: FilterFormValues = {
    holdTypes:
      filterParams.holdTypes.length > 0
        ? filterParams.holdTypes.map(val => ({
            value: val,
            label: capitalizeFirstLetterAndSpaceSnakeString(val),
          }))
        : DEFAULT_HOLD_TYPES.map(val => ({
            value: val,
            label: capitalizeFirstLetterAndSpaceSnakeString(val),
          })),
    dateRange: [
      filterParams.startDate
        ? dayjs(filterParams.startDate).toDate()
        : dayjs()
            .subtract(AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS, 'day')
            .toDate(),
      filterParams.endDate
        ? dayjs(filterParams.endDate).toDate()
        : dayjs().toDate(),
    ],
    minTotalImpact: filterParams.minTotalImpact,
    clients: clients.filter(client =>
      filterParams.clientIds.includes(client.id),
    ),
    insurers: insurers.filter(insurer =>
      filterParams.insurerIds.includes(insurer.id),
    ),
    partnerships: partnerships.filter(partnership =>
      filterParams.partnershipIds.includes(partnership.id),
    ),
  };

  const { handleSubmit, control, formState, reset } = useForm<FilterFormValues>(
    {
      mode: 'onChange',
      defaultValues: formValues,
      criteriaMode: 'all',
      resolver: yupResolver(filterValidationSchema),
    },
  );

  const { isValid, isDirty, errors } = formState;

  useEffect(() => {
    reset({
      holdTypes:
        filterParams.holdTypes.length > 0
          ? filterParams.holdTypes.map(val => ({
              value: val,
              label: capitalizeFirstLetterAndSpaceSnakeString(val),
            }))
          : DEFAULT_HOLD_TYPES.map(val => ({
              value: val,
              label: capitalizeFirstLetterAndSpaceSnakeString(val),
            })),
      dateRange: [
        filterParams.startDate
          ? dayjs(filterParams.startDate).toDate()
          : dayjs()
              .subtract(AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS, 'day')
              .toDate(),
        filterParams.endDate
          ? dayjs(filterParams.endDate).toDate()
          : dayjs().toDate(),
      ],
      minTotalImpact: filterParams.minTotalImpact,
      clients: clients.filter(client =>
        filterParams.clientIds.includes(client.id),
      ),
      insurers: insurers.filter(insurer =>
        filterParams.insurerIds.includes(insurer.id),
      ),
      partnerships: partnerships.filter(partnership =>
        filterParams.partnershipIds.includes(partnership.id),
      ),
    });
  }, [filterParams, clients, insurers, partnerships, reset]);

  const parseFormValues = (data: FilterFormValues): void => {
    const {
      holdTypes,
      dateRange,
      minTotalImpact,
      clients,
      insurers,
      partnerships,
    } = data;

    const formattedData = {
      ...filterParams,
      holdTypes: holdTypes.map(option => option.value),
      startDate: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
      endDate: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null,
      minTotalImpact: minTotalImpact || null,
      clientIds: clients.map(client => client.id),
      insurerIds: insurers.map(insurer => insurer.id),
      partnershipIds: partnerships.map(partnership => partnership.id),
    };

    setPaginationModel({
      page: 0,
      pageSize: AGGREGATE_HOLDS_DEFAULT_PAGE_SIZE,
    });
    setFilterParams(formattedData);
  };

  const onApplyClick = (): void => {
    handleSubmit(data => {
      parseFormValues(data);
      // Reset form state to mark current values as "clean"
      reset(data);
    })();
  };

  const sortedClients = [...clients].sort((a, b) =>
    selectSorterFunction(a.identifier, b.identifier),
  );
  const sortedInsurers = [...insurers].sort((a, b) =>
    selectSorterFunction(a.identifier, b.identifier),
  );
  const sortedPartnerships = [...partnerships].sort((a, b) =>
    selectSorterFunction(a.name, b.name),
  );

  return (
    <HHStack direction="row" spacing={2}>
      <HHStack
        direction="row"
        flexWrap="wrap"
        alignItems="flex-end"
        sx={{
          gap: 4,
          paddingBottom: 2,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Controller
          control={control}
          name="holdTypes"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              multiple
              id="hold-types-filter"
              value={value}
              options={holdTypesSelectOptions}
              size="small"
              sx={FLEX_FILTER_CONTAINER_SX}
              isOptionEqualToValue={(option, value): boolean =>
                option.value === value.value
              }
              limitTags={1}
              onChange={(_, newValue): void => onChange(newValue)}
              blurOnSelect={true}
              getOptionLabel={(option): string => option.label}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="standard"
                  label="Hold Reasons"
                  error={Object.keys(errors?.holdTypes ?? {}).length !== 0}
                />
              )}
            />
          )}
        />

        <ClientFilter
          control={control}
          clients={sortedClients}
          hasError={!!clientsError}
          loading={clientsLoading}
          errors={errors}
          sx={FLEX_FILTER_CONTAINER_SX}
          label="Clients"
        />

        <InsurerFilter
          control={control}
          insurers={sortedInsurers}
          hasError={!!insurersError}
          loading={insurersLoading}
          errors={errors}
          sx={FLEX_FILTER_CONTAINER_SX}
          label="Insurers"
        />

        <PartnershipFilter
          control={control}
          partnerships={sortedPartnerships}
          hasError={!!partnershipsError}
          loading={partnershipsLoading}
          errors={errors}
          sx={FLEX_FILTER_CONTAINER_SX}
          label="Partnerships"
        />

        <Controller
          control={control}
          name="dateRange"
          render={({ field: { onChange, value } }): JSX.Element => (
            <DateRangePicker
              value={value as [Date | null, Date | null]}
              onChange={(newValue): void => onChange(newValue)}
              renderInput={(startProps, endProps): JSX.Element => {
                const { label: _, ...restStart } = startProps;
                return (
                  <HHStack
                    direction="row"
                    spacing={2}
                    alignItems="flex-end"
                    minWidth={120}
                    maxWidth={180}
                  >
                    <HHTextField
                      hhVariant="variant-bypass"
                      variant="standard"
                      type="string"
                      label="Date Range"
                      InputLabelProps={{ sx: { color: 'gray' } }}
                      error={Object.keys(errors?.dateRange ?? {}).length !== 0}
                      helperText={errors.dateRange?.message}
                      {...restStart}
                    />
                    <HHTypography hhVariant="body1"> to </HHTypography>
                    <HHTextField
                      hhVariant="variant-bypass"
                      variant="standard"
                      type="string"
                      InputLabelProps={{ sx: { color: 'gray' } }}
                      error={Object.keys(errors?.dateRange ?? {}).length !== 0}
                      helperText={errors.dateRange?.message}
                      {...endProps}
                    />
                  </HHStack>
                );
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="minTotalImpact"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="standard"
              type="number"
              label="Min $ Impact"
              value={value || ''}
              onChange={(e): void =>
                onChange(e.target.value ? Number(e.target.value) : null)
              }
              error={Object.keys(errors?.minTotalImpact ?? {}).length !== 0}
              helperText={errors.minTotalImpact?.message}
              sx={{ minWidth: 80, maxWidth: 100 }}
            />
          )}
        />
      </HHStack>
      <HHStack
        direction="row"
        spacing={1}
        alignSelf="flex-end"
        alignItems="center"
      >
        <HHButton
          size="small"
          type="submit"
          hhVariant="contained"
          onClick={onApplyClick}
          disabled={!isValid || !isDirty || Object.keys(errors).length > 0}
        >
          Apply
        </HHButton>
        <HHStack
          sx={{
            opacity: !isDirty && isValid ? 1 : 0.4,
            pointerEvents: !isDirty && isValid ? 'auto' : 'none',
          }}
        >
          <TextCopier
            text={getCurrentUrl()}
            displayText={false}
            alwaysDisplayIcon={true}
            tooltipText={
              !isDirty && isValid
                ? 'Copy filtered URL to clipboard'
                : 'Apply filters to copy URL'
            }
            copiedToolTipText="Filtered URL copied!"
          />
        </HHStack>
      </HHStack>
    </HHStack>
  );
};

export default AggregateHoldsFilters;
